/**
 * Admin → Users export. One row per user with every organization's role and
 * the full location list (the table abbreviates to "Name +3"), so the file can
 * be filtered and shared as-is.
 */

const ORG_WIDE_ROLES = new Set(['orgAdmin', 'orgViewer']);

const ROLE_LABELS = {
  orgAdmin: 'Org admin',
  orgViewer: 'Org viewer',
  locationAdmin: 'Location admin',
  locationUser: 'Location user',
  locationViewer: 'Location viewer',
};

const roleLabel = (role) => ROLE_LABELS[role] || role || '';

/**
 * @param {Array} users - userSettings docs (already filtered for visibility)
 * @param {Array} locations - [{ id, name }]
 * @param {Object} orgNames - org code → display name
 */
export function userExportRows(users, locations = [], orgNames = {}) {
  const locName = new Map((Array.isArray(locations) ? locations : []).map((l) => [String(l.id), l.name]));
  return users.map((u) => {
    const access = u.orgAccess && typeof u.orgAccess === 'object' ? u.orgAccess : {};
    const codes = Object.keys(access);
    const names = new Set();
    let orgWide = false;
    codes.forEach((code) => {
      if (ORG_WIDE_ROLES.has(access[code]?.role)) orgWide = true;
      (access[code]?.locationIds || []).forEach((id) => {
        const n = locName.get(String(id));
        if (n) names.add(n);
      });
    });
    return {
      Email: u.email || '',
      'First Name': u.firstName || '',
      'Last Name': u.lastName || '',
      'Job Title': u.jobTitle || '',
      Organizations: codes.map((c) => orgNames[c] || c).join('; '),
      'Org Roles': codes.map((c) => `${c}: ${roleLabel(access[c]?.role)}`).join('; '),
      Locations: orgWide ? 'All locations' : [...names].sort((a, b) => a.localeCompare(b)).join('; '),
      'Location Count': orgWide ? '' : names.size,
      'Lodgeic Admin': u.isAdmin === 1 || u.isAdmin === true ? 'Yes' : 'No',
      Status: u.isDeleted === 1 || u.isDeleted === true ? 'Deleted' : 'Active',
    };
  });
}

/** users-2026-09-23.xlsx, or users-whg-2026-09-23.xlsx for a search. */
export function userExportFileName(search, today = new Date()) {
  const date = today.toISOString().slice(0, 10);
  const slug = String(search || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
  return `users${slug ? `-${slug}` : ''}-${date}.xlsx`;
}

/** Write rows to an .xlsx and download it. SheetJS loads on demand. */
export async function downloadUserExport(rows, fileName) {
  const XLSX = await import('xlsx');
  const sheet = XLSX.utils.json_to_sheet(rows);
  const headers = rows.length ? Object.keys(rows[0]) : [];
  sheet['!cols'] = headers.map((h) => ({
    wch: Math.min(60, Math.max(h.length, ...rows.map((r) => String(r[h] ?? '').length)) + 2),
  }));
  if (rows.length) sheet['!autofilter'] = { ref: sheet['!ref'] };
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Users');
  XLSX.writeFile(book, fileName);
}
